import React, { useState, useEffect } from "react";
import { Dropdown } from "semantic-ui-react";
import { GeiEmissionFactor, GeiFactorLibrary } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import "./GEIFactorsTable.scss";

const geiFactorController     = new GeiEmissionFactor();
const geiLibraryController    = new GeiFactorLibrary();

// custom_organism/custom_source_url/custom_notes los completa cualquier
// usuario con acceso al factor (no solo un admin) y terminan en la hoja de
// Excel de "Advertencias" — si un valor empieza con =, +, - o @, Excel puede
// interpretarlo como fórmula (o comando DDE en versiones viejas) al abrirlo.
function sanitizeForSpreadsheet(value) {
  const str = value === null || value === undefined ? "" : String(value);
  return /^[=+\-@]/.test(str) ? `'${str}` : str;
}

// Fallback para fuentes sin entrada en la biblioteca (lfo, carbon no están en el Excel)
const COMB_FALLBACK = {
  defra: { lfo:2.5000, carbon:2.4000 },
  ghg:   { lfo:2.5000, carbon:2.4000 },
  ipcc:  { lfo:2.5000, carbon:2.4000 },
};

// ─────────────────────────────────────────────────────────────────────────────
const COUNTRY_NAMES = {
  CA:'Canadá',US:'EE.UU.',MX:'México',GT:'Guatemala',SV:'El Salvador',HN:'Honduras',
  NI:'Nicaragua',CR:'Costa Rica',PA:'Panamá',DO:'Rep. Dom.',HT:'Haití',CU:'Cuba',
  AR:'Argentina',BO:'Bolivia',BR:'Brasil',CL:'Chile',CO:'Colombia',EC:'Ecuador',
  PE:'Perú',PY:'Paraguay',UY:'Uruguay',VE:'Venezuela',BD:'Bangladesh',CN:'China',
  ID:'Indonesia',IN:'India',KH:'Camboya',LK:'Sri Lanka',MM:'Myanmar',MY:'Malasia',
  PK:'Pakistán',TH:'Tailandia',VN:'Vietnam',DE:'Alemania',ES:'España',FR:'Francia',
  GB:'Reino Unido',IT:'Italia',PT:'Portugal',
};

const STD_NAMES = { defra:'DEFRA 2025', ghg:'GHG Protocol AR6', ipcc:'IPCC AR6', custom:'Manual' };
const STD_YEAR  = { defra:'ene-2025', ghg:'2023', ipcc:'2021', custom:'—' };
// Mapeo UI key ↔ DB standard_key
const UI_TO_DB  = { defra:'defra', ghg:'ghg_ar6', ipcc:'ipcc_ar6', custom:null };
const DB_TO_UI  = { defra:'defra', ghg_ar6:'ghg', ipcc_ar6:'ipcc' };

// ─────────────────────────────────────────────────────────────────────────────
// ROW DEFINITIONS — source_key matches DB field in GeiEmissionFactor
// type: 'elec'=red eléctrica | 'ren'=PPA renovable (siempre 0) | 'comb'=combustión
// ck: clave en objeto COMB para lookup de factor
// ─────────────────────────────────────────────────────────────────────────────
const ROWS = [
  { g:"Electricidad Estándar",   source_key:"elec_std_combinada",         l:"Electricidad Est. · Combinada",      u:"kWh", sc:2, type:"elec" },
  { g:"Electricidad Estándar",   source_key:"elec_std_domestico",         l:"Electricidad Est. · Doméstico",      u:"kWh", sc:2, type:"elec" },
  { g:"Electricidad Estándar",   source_key:"elec_std_produccion",        l:"Electricidad Est. · Producción",     u:"kWh", sc:2, type:"elec" },
  { g:"Energías Renovables PPA", source_key:"elec_ren_combinada",         l:"Renovables PPA · Combinada",         u:"kWh", sc:2, type:"ren"  },
  { g:"Energías Renovables PPA", source_key:"elec_ren_domestico",         l:"Renovables PPA · Doméstico",         u:"kWh", sc:2, type:"ren"  },
  { g:"Energías Renovables PPA", source_key:"elec_ren_produccion",        l:"Renovables PPA · Producción",        u:"kWh", sc:2, type:"ren"  },
  { g:"Gas Natural",             source_key:"gas_natural_combinado",      l:"Gas Natural · Combinado",            u:"m³",  sc:1, type:"comb", ck:"gas_natural" },
  { g:"Gas Natural",             source_key:"gas_natural_cogeneracion",   l:"Gas Natural · Cogeneración",         u:"m³",  sc:1, type:"comb", ck:"gas_natural" },
  { g:"Gas Natural",             source_key:"gas_natural_produccion",     l:"Gas Natural · Producción y Op.",     u:"m³",  sc:1, type:"comb", ck:"gas_natural" },
  { g:"Gas Natural",             source_key:"gas_natural_domestico",      l:"Gas Natural · Doméstico",            u:"m³",  sc:1, type:"comb", ck:"gas_natural" },
  { g:"GLP",                     source_key:"glp_combinado",              l:"GLP · Combinado",                    u:"L",   sc:1, type:"comb", ck:"glp" },
  { g:"GLP",                     source_key:"glp_cogeneracion",           l:"GLP · Cogeneración",                 u:"L",   sc:1, type:"comb", ck:"glp" },
  { g:"GLP",                     source_key:"glp_produccion",             l:"GLP · Producción y Op.",             u:"L",   sc:1, type:"comb", ck:"glp" },
  { g:"GLP",                     source_key:"glp_domestico",              l:"GLP · Doméstico",                    u:"L",   sc:1, type:"comb", ck:"glp" },
  { g:"Heavy Fuel Oil",          source_key:"heavy_fuel_oil",             l:"Heavy Fuel Oil",                     u:"L",   sc:1, type:"comb", ck:"hfo" },
  { g:"Light Fuel Oil",          source_key:"light_fuel_oil",             l:"Light Fuel Oil",                     u:"L",   sc:1, type:"comb", ck:"lfo" },
  { g:"Carbón",                  source_key:"carbon",                     l:"Carbón",                             u:"kg",  sc:1, type:"comb", ck:"carbon" },
  { g:"Diesel",                  source_key:"diesel_combinado",           l:"Diesel · Combinado",                 u:"L",   sc:1, type:"comb", ck:"diesel" },
  { g:"Diesel",                  source_key:"diesel_cogeneracion",        l:"Diesel · Cogeneración",              u:"L",   sc:1, type:"comb", ck:"diesel" },
  { g:"Diesel",                  source_key:"diesel_produccion",          l:"Diesel · Producción y Op.",          u:"L",   sc:1, type:"comb", ck:"diesel" },
  { g:"Diesel",                  source_key:"diesel_montacargas",         l:"Diesel · Montacargas",               u:"L",   sc:1, type:"comb", ck:"diesel" },
  { g:"Biodiesel",               source_key:"biodiesel_combinado",        l:"Biodiesel · Combinado",              u:"L",   sc:1, type:"comb", ck:"biodiesel" },
  { g:"Biodiesel",               source_key:"biodiesel_cogeneracion",     l:"Biodiesel · Cogeneración",           u:"L",   sc:1, type:"comb", ck:"biodiesel" },
  { g:"Biodiesel",               source_key:"biodiesel_produccion",       l:"Biodiesel · Producción y Op.",       u:"L",   sc:1, type:"comb", ck:"biodiesel" },
  { g:"Biodiesel",               source_key:"biodiesel_montacargas",      l:"Biodiesel · Montacargas",            u:"L",   sc:1, type:"comb", ck:"biodiesel" },
  { g:"Gasolina",                source_key:"gasolina_combinado",         l:"Gasolina · Combinado",               u:"L",   sc:1, type:"comb", ck:"gasolina" },
  { g:"Gasolina",                source_key:"gasolina_cogeneracion",      l:"Gasolina · Cogeneración",            u:"L",   sc:1, type:"comb", ck:"gasolina" },
  { g:"Gasolina",                source_key:"gasolina_produccion",        l:"Gasolina · Producción y Op.",        u:"L",   sc:1, type:"comb", ck:"gasolina" },
  { g:"Gasolina",                source_key:"gasolina_montacargas",       l:"Gasolina · Montacargas",             u:"L",   sc:1, type:"comb", ck:"gasolina" },
  { g:"Biocombustible",          source_key:"biocombustible_combinado",   l:"Biocombustible · Combinado",         u:"L",   sc:1, type:"comb", ck:"biodiesel" },
  { g:"Biocombustible",          source_key:"biocombustible_cogeneracion",l:"Biocombustible · Cogeneración",      u:"L",   sc:1, type:"comb", ck:"biodiesel" },
  { g:"Biocombustible",          source_key:"biocombustible_produccion",  l:"Biocombustible · Producción y Op.",  u:"L",   sc:1, type:"comb", ck:"biodiesel" },
  { g:"Biocombustible",          source_key:"biocombustible_montacargas", l:"Biocombustible · Montacargas",       u:"L",   sc:1, type:"comb", ck:"biodiesel" },
  { g:"Biomasa",                 source_key:"biomasa",                    l:"Biomasa",                            u:"kg",  sc:1, type:"comb", ck:"biomasa" },
  { g:"Vapor",                   source_key:"vapor",                      l:"Vapor",                              u:"kg",  sc:2, type:"comb", ck:"vapor" },
  { g:"Vehículos",               source_key:"gas_natural_vehiculos",      l:"Gas Natural · Vehículos",            u:"m³",  sc:1, type:"comb", ck:"gas_natural" },
  { g:"Vehículos",               source_key:"glp_vehiculos",              l:"GLP · Vehículos",                    u:"L",   sc:1, type:"comb", ck:"glp" },
  { g:"Vehículos",               source_key:"diesel_vehiculos",           l:"Diesel · Vehículos",                 u:"L",   sc:1, type:"comb", ck:"diesel" },
  { g:"Vehículos",               source_key:"biodiesel_vehiculos",        l:"Biodiesel · Vehículos",              u:"L",   sc:1, type:"comb", ck:"biodiesel" },
  { g:"Vehículos",               source_key:"gasolina_vehiculos",         l:"Gasolina · Vehículos",               u:"L",   sc:1, type:"comb", ck:"gasolina" },
  { g:"Vehículos",               source_key:"etanol_vehiculos",           l:"Etanol · Vehículos",                 u:"L",   sc:1, type:"comb", ck:"gasolina" },
  { g:"Vehículos",               source_key:"electricidad_vehiculos",     l:"Electricidad · Vehículos",           u:"kWh", sc:1, type:"elec"  },
];

const PREVIEW = {
  defra: [
    ['Gas Natural','2.04542','m³','⚠ +7.6% vs GHG Protocol'],
    ['Diesel',     '2.6820', 'L', ''],['GLP','1.5570','L',''],['Gasolina','2.3110','L',''],
    ['HFO',        '3.1790', 'L', ''],['Biomasa','0.0180','kg','Solo CH₄+N₂O'],
    ['Biodiesel',  '0.1687', 'L', 'Solo no-biogénico'],['Vapor','0.1660','kg','Alcance 2'],
  ],
  ghg: [
    ['Gas Natural','1.9000','m³',''],['Diesel','2.6800','L',''],['GLP','1.5400','L',''],
    ['Gasolina','2.2990','L',''],['HFO','3.1490','L',''],['Biomasa','0.0180','kg','Solo CH₄+N₂O'],
    ['Biodiesel','0.1780','L',''],['Vapor','0.1800','kg','Alcance 2'],
  ],
  ipcc: [
    ['Gas Natural','1.9300','m³','GWPs AR6'],['Diesel','2.6630','L',''],['GLP','1.5300','L',''],
    ['Gasolina','2.2960','L',''],['HFO','3.1400','L',''],['Biomasa','0.0180','kg','Solo CH₄+N₂O'],
    ['Biodiesel','0.1750','L',''],['Vapor','0.1800','kg','Alcance 2'],
  ],
  custom: [
    ['Gas Natural','(manual)','m³',''],['Diesel','(manual)','L',''],
    ['GLP','(manual)','L',''],['Gasolina','(manual)','L',''],['Biomasa','(manual)','kg',''],
  ],
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const STD_BD = { defra:'bd-defra', ghg:'bd-ghg', ipcc:'bd-ipcc', custom:'bd-custom', pais:'bd-pais' };

function getFColor(std) {
  return ({defra:'var(--gfc-c-defra)',ghg:'var(--gfc-c-ghg)',ipcc:'var(--gfc-c-ipcc)',pais:'var(--gfc-c-pais)',custom:'var(--gfc-c-custom)'})[std]||'var(--gfc-navy)';
}
function getFBg(std) {
  return ({defra:'var(--gfc-c-defra-bg)',ghg:'var(--gfc-c-ghg-bg)',ipcc:'var(--gfc-c-ipcc-bg)',pais:'var(--gfc-c-pais-bg)',custom:'var(--gfc-c-custom-bg)'})[std]||'var(--gfc-sky-pale)';
}
function getStdBorder(std) {
  return ({defra:'#6EE7B7',ghg:'#93C5FD',ipcc:'#C4B5FD',pais:'#67E8F9',custom:'#FCA5A5'})[std]||'#E2E8F0';
}
function getConfIcon(conf) {
  return ({alto:'✅',medio_alto:'🔵',medio:'⚠',verificar:'🔴',proxy:'❌'})[conf]||'⚠';
}
function getDotCls(conf, isCustom, next_review_due) {
  if (isCustom) return 'sd-custom';
  // Si la fecha de próxima revisión ya venció → amarillo independientemente de la confianza
  if (next_review_due) {
    const [y, m] = next_review_due.split('-').map(Number);
    const due = new Date(y, m - 1, 1);
    if (due <= new Date()) return 'sd-warn';
  }
  return ({alto:'sd-ok',medio_alto:'sd-ok',medio:'sd-warn',verificar:'sd-custom',proxy:'sd-custom'})[conf]||'sd-warn';
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export function GEIFactorsTable({ site, siteName = "" }) {
  const { accessToken, user } = useAuth();

  const [factors,      setFactors]      = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [initialized,  setInitialized]  = useState(false);
  const [combStd,      setCombStd]      = useState('ghg');
  const [perRowStd,    setPerRowStd]    = useState({});
  const [manualVals,   setManualVals]   = useState({});
  const [siteCountry,  setSiteCountry]  = useState('');
  const [editing,      setEditing]      = useState(null);
  const [editInput,    setEditInput]    = useState('');
  const [editModal,    setEditModal]    = useState(null); // { sk, value, organism, source_url, notes }
  const [showModal,    setShowModal]    = useState(false);
  const [modalStd,     setModalStd]     = useState('ghg');
  const [COMB,         setCOMB]         = useState({ defra:{}, ghg:{}, ipcc:{} });
  const [COMB_META,    setCOMB_META]    = useState({ defra:null, ghg:null, ipcc:null });
  const [ELEC,         setELEC]         = useState({});
  const [isDirty,      setIsDirty]      = useState(false);

  // Index of DB factors by source_key
  const factorMap = {};
  factors.forEach(f => { factorMap[f.source_key] = f; });

  useEffect(() => {
    if (accessToken) loadLibrary();
  }, [accessToken]); // eslint-disable-line

  useEffect(() => {
    if (site && accessToken) {
      if (isDirty) toast.info('Los cambios sin guardar fueron descartados al cambiar de sitio.');
      loadFactors();
    }
  }, [site, accessToken]); // eslint-disable-line

  // Alerta al cerrar/recargar pestaña con cambios pendientes
  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const loadLibrary = async () => {
    try {
      const [combResp, elecResp] = await Promise.all([
        geiLibraryController.getCombustionFactors(accessToken),
        geiLibraryController.getElectricityFactors(accessToken),
      ]);
      if (combResp.code === 200) {
        const merged = {};
        ['defra','ghg','ipcc'].forEach(k => {
          merged[k] = { ...COMB_FALLBACK[k], ...combResp.combustion[k] };
        });
        setCOMB(merged);
        setCOMB_META(combResp.combustionMeta || { defra:null, ghg:null, ipcc:null });
      }
      if (elecResp.code === 200) setELEC(elecResp.electricity);
    } catch (e) {
      console.error('Error al cargar biblioteca GEI:', e);
    }
  };

  const loadFactors = async () => {
    setLoading(true);
    try {
      const resp = await geiFactorController.getFactorsBySite(accessToken, site);
      if (resp.code === 200) {
        setFactors(resp.factors);
        setInitialized(resp.factors.length > 0);

        const firstComb = resp.factors.find(f => f.standard_key && !f.is_custom);
        const globalUiStd = DB_TO_UI[firstComb?.standard_key] || 'ghg';
        setCombStd(globalUiStd);

        const firstElec = resp.factors.find(f => f.country_code);
        setSiteCountry(firstElec?.country_code || '');

        // Reconstruir perRowStd y manualVals desde la DB
        const newPerRowStd = {};
        const newManualVals = {};
        for (const f of resp.factors) {
          if (f.is_custom) {
            newPerRowStd[f.source_key] = 'custom';
            newManualVals[f.source_key] = f.factor_value;
          } else if (f.standard_key) {
            const uiStd = DB_TO_UI[f.standard_key];
            if (uiStd && uiStd !== globalUiStd) {
              newPerRowStd[f.source_key] = uiStd;
            }
          }
        }
        setPerRowStd(newPerRowStd);
        setManualVals(newManualVals);
        setEditing(null);
        setIsDirty(false);
      }
    } catch (e) {
      console.error('Error al cargar factores GEI:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleInitialize = async () => {
    setLoading(true);
    try {
      const resp = await geiFactorController.initializeDefaultFactors(accessToken, site);
      if (resp.code === 201) {
        toast.success('Factores GEI inicializados');
        await loadFactors();
      }
    } catch (e) {
      toast.error('Error al inicializar factores GEI');
    } finally {
      setLoading(false);
    }
  };

  // ─── Row state helpers ──────────────────────────────────────────────────────
  const getRowState = (row) => {
    const sk = row.source_key;
    if (row.type === 'ren') return { factor: 0, std: 'ghg', isCustom: false };

    const isElec = row.type === 'elec';
    if (isElec) {
      const e = siteCountry && ELEC[siteCountry];
      return { factor: e ? e.f : null, std: 'pais', isCustom: false };
    }

    // Manual override takes priority
    if (manualVals[sk] !== undefined) {
      return { factor: manualVals[sk], std: 'custom', isCustom: true };
    }

    const std = perRowStd[sk] || combStd;
    if (std === 'custom') {
      return { factor: factorMap[sk]?.factor_value ?? null, std: 'custom', isCustom: true };
    }
    return { factor: COMB[std]?.[row.ck] ?? null, std, isCustom: false };
  };

  // ─── Interaction handlers ───────────────────────────────────────────────────
  const onCombStdChange = (std) => {
    setCombStd(std);
    setIsDirty(true);
    // Clear per-row overrides (keep only rows that were manually set to custom)
    setPerRowStd(prev => {
      const next = {};
      Object.keys(prev).forEach(sk => { if (prev[sk] === 'custom') next[sk] = 'custom'; });
      return next;
    });
    if (std !== 'custom') setManualVals({});
  };

  const onRowStdChange = (row, newStd) => {
    const sk = row.source_key;
    setPerRowStd(prev => ({ ...prev, [sk]: newStd }));
    setIsDirty(true);
    if (newStd !== 'custom') {
      setManualVals(prev => { const n = { ...prev }; delete n[sk]; return n; });
    }
  };

  const enableEdit = (sk) => {
    const row = ROWS.find(r => r.source_key === sk);
    if (!row || row.type !== 'comb') return;
    const { factor } = getRowState(row);
    const db = factorMap[sk];
    setEditModal({
      sk,
      value:      factor !== null ? String(factor) : '',
      organism:   db?.custom_organism   || '',
      source_url: db?.custom_source_url || '',
      notes:      db?.custom_notes      || '',
    });
  };

  const commitEditModal = () => {
    if (!editModal) return;
    const val = parseFloat(editModal.value);
    if (isNaN(val) || val < 0) { toast.error('Ingresá un valor numérico válido'); return; }
    if (!editModal.organism.trim())   { toast.error('El organismo fuente es obligatorio'); return; }
    if (!editModal.source_url.trim()) { toast.error('La URL de referencia es obligatoria'); return; }
    setManualVals(prev => ({ ...prev, [editModal.sk]: val }));
    setPerRowStd(prev => ({ ...prev, [editModal.sk]: 'custom' }));
    // Guardar cita en factorMap local para que handleSaveAll la envíe
    const db = factorMap[editModal.sk];
    if (db) {
      db.custom_organism   = editModal.organism.trim();
      db.custom_source_url = editModal.source_url.trim();
      db.custom_notes      = editModal.notes.trim();
    }
    setIsDirty(true);
    setEditModal(null);
  };

  const handleSaveAll = async () => {
    if (!initialized) return;
    setSaving(true);
    try {
      const promises = [];
      for (const row of ROWS) {
        const db = factorMap[row.source_key];
        if (!db) continue;
        const { factor, std, isCustom } = getRowState(row);
        const isElec = row.type === 'elec';
        promises.push(
          geiFactorController.updateFactor(accessToken, db._id, {
            factor_value:      factor,
            is_custom:         isCustom,
            standard_key:      isCustom || isElec ? null : (UI_TO_DB[std] || null),
            country_code:      isElec ? siteCountry || null : null,
            custom_organism:   isCustom ? (db.custom_organism   || null) : null,
            custom_source_url: isCustom ? (db.custom_source_url || null) : null,
            custom_notes:      isCustom ? (db.custom_notes      || null) : null,
          })
        );
      }
      await Promise.all(promises);
      toast.success(`✅ Factores guardados`);
      setIsDirty(false);
      await loadFactors();
    } catch (e) {
      toast.error('Error al guardar factores');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyModal = () => {
    onCombStdChange(modalStd);
    setShowModal(false);
    if (modalStd !== 'custom') {
      const n = ROWS.filter(r => r.type === 'comb').length;
      toast.success(`${STD_NAMES[modalStd]} aplicado a ${n} fuentes de combustión`);
    }
  };

  // ─── Export PDF ─────────────────────────────────────────────────────────────
  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pw = doc.internal.pageSize.getWidth();
    const margin = 24;
    let y = 36;

    // Encabezado
    doc.setFontSize(13).setFont(undefined, 'bold').setTextColor(26, 53, 98);
    doc.text('Factores de Emision GEI - Configuracion del sitio', margin, y); y += 16;

    if (siteName) {
      doc.setFontSize(10).setFont(undefined, 'normal').setTextColor(26, 53, 98);
      doc.text(siteName.toUpperCase(), margin, y); y += 13;
    }

    // Línea de metadata — formato: Factores: [std] [ver] · Electricidad: [org] ([pais]) [year] · Verificado: [date] · Por: [user]
    const STD_SHORT = { defra:'DEFRA', ghg:'GHG Protocol', ipcc:'IPCC', custom:'Manual' };
    const metaSegments = [];

    if (combStd !== 'custom') {
      const ver = STD_YEAR[combStd] || '';
      metaSegments.push(`Factores: ${STD_SHORT[combStd]} ${ver}`.trim());
    }

    if (siteCountry && ELEC[siteCountry]) {
      const e = ELEC[siteCountry];
      metaSegments.push(`Electricidad: ${e.org} (${COUNTRY_NAMES[siteCountry] || siteCountry}) ${e.y}`);
      if (e.verified_at) metaSegments.push(`Verificado: ${e.verified_at}`);
    } else if (COMB_META[combStd]?.verified_at) {
      metaSegments.push(`Verificado: ${COMB_META[combStd].verified_at}`);
    }

    const userName = [user?.firstname, user?.lastname].filter(Boolean).join(' ') || user?.email || '';
    if (userName) metaSegments.push(`Por: ${userName}`);

    if (metaSegments.length) {
      doc.setFontSize(8.5).setFont(undefined, 'normal').setTextColor(80, 100, 140);
      doc.text(metaSegments.join('   \xB7   '), margin, y); y += 13;
    }

    doc.setFontSize(7.5).setFont(undefined, 'normal').setTextColor(150, 150, 150);
    doc.text(`Generado el ${new Date().toLocaleDateString('es-ES', { day:'2-digit', month:'long', year:'numeric' })}`, margin, y);
    y += 14;
    doc.setDrawColor(210, 215, 225).line(margin, y, pw - margin, y); y += 10;

    // Tabla principal
    const CONF_LABEL = { alto:'Verificado', medio_alto:'Plausible', medio:'Verificar anual', verificar:'Pendiente', proxy:'Proxy IEA' };
    const tableRows = ROWS.map(row => {
      const { factor, std, isCustom } = getRowState(row);
      const isElec = row.type === 'elec';
      const isRen  = row.type === 'ren';

      let referencia = '-';
      let conf       = '-';
      if (isRen)  { referencia = 'PPA - Factor 0'; conf = 'Verificado'; }
      else if (isElec && siteCountry && ELEC[siteCountry]) {
        const e = ELEC[siteCountry];
        referencia = `${COUNTRY_NAMES[siteCountry]} - ${e.org} ${e.y}`;
        conf       = CONF_LABEL[e.confidence] || e.confidence;
      } else if (!isElec) {
        referencia = isCustom ? 'Manual' : (STD_NAMES[std] || std);
        conf       = isCustom ? 'Manual' : 'Verificado';
      }

      return [
        row.l,
        row.u,
        referencia,
        factor !== null ? Number(factor).toFixed(4) : '-',
        `Alcance ${row.sc}`,
        conf,
      ];
    });

    autoTable(doc, {
      startY: y,
      head: [['Fuente', 'Unidad', 'Referencia', 'Factor (kgCO2e/ud)', 'Alcance', 'Confianza']],
      body: tableRows,
      tableWidth:  pw - margin * 2,
      styles:      { fontSize: 7.5, cellPadding: 4 },
      headStyles:  { fillColor: [45, 95, 138], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 180 },
        1: { cellWidth: 42,  halign: 'center' },
        2: { cellWidth: 250 },
        3: { cellWidth: 90,  halign: 'right' },
        4: { cellWidth: 70,  halign: 'center' },
        5: { cellWidth: 'auto' },
      },
      alternateRowStyles: { fillColor: [245, 248, 252] },
      didParseCell: (data) => {
        // Tinte azul para filas de electricidad
        const rowIdx = data.row.index;
        if (ROWS[rowIdx]?.type === 'elec' && data.section === 'body') {
          data.cell.styles.fillColor = [220, 232, 245];
        }
      },
    });

    // ── Sección de advertencias ──────────────────────────────────────────────
    const warnings = [];

    // Electricidad — país con confidence verificar/proxy
    if (siteCountry && ELEC[siteCountry]) {
      const e = ELEC[siteCountry];
      if (['verificar', 'proxy'].includes(e.confidence)) {
        const elecRows = ROWS.filter(r => r.type === 'elec').map(r => r.l).join(', ');
        warnings.push({
          fuente:  `Electricidad de red - ${COUNTRY_NAMES[siteCountry]}`,
          estado:  CONF_LABEL[e.confidence],
          nota:    e.status === 'proxy'
            ? `Factor proxy IEA (${e.y}). Sin publicacion oficial nacional. Documentar como estimado en auditorias. Fuente: ${e.org}. Proxima revision: ${e.next_review}.`
            : `Factor pendiente de confirmacion. Fuente: ${e.org}. Verificado: ${e.verified_at || '-'}. Proxima revision: ${e.next_review}.`,
        });
      }
    }

    // Factores con library_ref_id y confidence crítica
    factors.forEach(f => {
      const lib = f.library_ref_id;
      if (!lib) return;
      if (!['verificar', 'proxy'].includes(lib.confidence)) return;
      const row = ROWS.find(r => r.source_key === f.source_key);
      warnings.push({
        fuente: row?.l || f.source_key,
        estado: CONF_LABEL[lib.confidence] || lib.confidence,
        nota:   lib.notes || `Fuente: ${lib.organism || '-'}. Año: ${lib.data_year || '-'}. Proxima revision: ${lib.next_review_due || '-'}.`,
      });
    });

    // Factores manuales
    factors.forEach(f => {
      if (!f.is_custom) return;
      const row = ROWS.find(r => r.source_key === f.source_key);
      const citaParts = [];
      if (f.custom_organism)   citaParts.push(`Fuente: ${f.custom_organism}`);
      if (f.custom_source_url) citaParts.push(`URL: ${f.custom_source_url}`);
      if (f.custom_notes)      citaParts.push(f.custom_notes);
      warnings.push({
        fuente: row?.l || f.source_key,
        estado: 'Manual',
        nota: citaParts.length
          ? citaParts.join(' | ')
          : 'Sin fuente documentada. No apto para auditorias GHG Protocol sin respaldo adicional.',
      });
    });

    if (warnings.length > 0) {
      let wy = doc.lastAutoTable.finalY + 20;
      const pageH = doc.internal.pageSize.getHeight();
      if (wy + 60 > pageH) { doc.addPage(); wy = margin; }

      doc.setFontSize(10).setFont(undefined, 'bold').setTextColor(180, 40, 40);
      doc.text('Advertencias sobre factores de emision', margin, wy); wy += 14;

      doc.setFontSize(7.5).setFont(undefined, 'normal').setTextColor(100, 100, 100);
      doc.text('Los siguientes factores requieren verificacion adicional antes de usar este reporte en una auditoria.', margin, wy);
      wy += 12;

      autoTable(doc, {
        startY: wy,
        tableWidth: pw - margin * 2,
        head: [['Fuente', 'Estado', 'Nota explicativa']],
        body: warnings.map(w => [w.fuente, w.estado, w.nota]),
        styles:      { fontSize: 7.5, cellPadding: 4 },
        headStyles:  { fillColor: [180, 60, 60], textColor: 255, fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 160 },
          1: { cellWidth: 90 },
          2: { cellWidth: 'auto' },
        },
        alternateRowStyles: { fillColor: [255, 245, 245] },
      });
    }

    // Pie de página en todas las páginas
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(7).setTextColor(180, 180, 180);
      doc.text(`Sustemia - Factores GEI | Pag. ${i} de ${totalPages}`, pw / 2, doc.internal.pageSize.getHeight() - 14, { align: 'center' });
    }

    doc.save(`factores-gei-${new Date().toISOString().slice(0,10)}.pdf`);
  };

  const exportXLSX = () => {
    const CONF_LABEL = { alto:'Verificado', medio_alto:'Plausible', medio:'Verificar anual', verificar:'Pendiente', proxy:'Proxy IEA' };
    const wb = XLSX.utils.book_new();

    // ── Hoja 1: Factores ──────────────────────────────────────────────────────
    const header = [['Fuente', 'Unidad', 'Referencia', 'Factor (kgCO2e/ud)', 'Alcance', 'Confianza', 'Estado']];
    const dataRows = ROWS.map(row => {
      const { factor, std, isCustom } = getRowState(row);
      const isElec = row.type === 'elec';
      const isRen  = row.type === 'ren';

      let referencia = '-';
      let conf = '-';
      let estado = '-';

      if (isRen) {
        referencia = 'PPA - Factor 0'; conf = 'Verificado'; estado = 'official';
      } else if (isElec && siteCountry && ELEC[siteCountry]) {
        const e = ELEC[siteCountry];
        referencia = `${COUNTRY_NAMES[siteCountry]} - ${e.org} ${e.y}`;
        conf   = CONF_LABEL[e.confidence] || e.confidence;
        estado = e.status;
      } else if (!isElec) {
        referencia = isCustom ? 'Manual' : (STD_NAMES[std] || std);
        conf   = isCustom ? 'Manual' : 'Verificado';
        estado = isCustom ? 'custom' : 'official';
      }

      return [
        row.l,
        row.u,
        referencia,
        factor !== null ? Number(factor) : null,
        `Alcance ${row.sc}`,
        conf,
        estado,
      ];
    });

    const ws1 = XLSX.utils.aoa_to_sheet([...header, ...dataRows]);
    ws1['!cols'] = [{ wch:35 }, { wch:8 }, { wch:40 }, { wch:18 }, { wch:10 }, { wch:16 }, { wch:12 }];
    XLSX.utils.book_append_sheet(wb, ws1, 'Factores GEI');

    // ── Hoja 2: Advertencias ──────────────────────────────────────────────────
    const warnings = [];
    if (siteCountry && ELEC[siteCountry]) {
      const e = ELEC[siteCountry];
      if (['verificar', 'proxy'].includes(e.confidence)) {
        warnings.push([
          `Electricidad - ${COUNTRY_NAMES[siteCountry]}`,
          CONF_LABEL[e.confidence],
          e.status === 'proxy'
            ? `Proxy IEA (${e.y}). Sin publicacion oficial. Fuente: ${e.org}. Proxima revision: ${e.next_review}.`
            : `Pendiente confirmacion. Fuente: ${e.org}. Verificado: ${e.verified_at || '-'}.`,
        ]);
      }
    }
    factors.forEach(f => {
      const lib = f.library_ref_id;
      if (!lib || !['verificar','proxy'].includes(lib.confidence)) return;
      const row = ROWS.find(r => r.source_key === f.source_key);
      warnings.push([
        row?.l || f.source_key,
        CONF_LABEL[lib.confidence] || lib.confidence,
        sanitizeForSpreadsheet(lib.notes || `Fuente: ${lib.organism || '-'}. Año: ${lib.data_year || '-'}.`),
      ]);
    });

    // Factores manuales
    factors.forEach(f => {
      if (!f.is_custom) return;
      const row = ROWS.find(r => r.source_key === f.source_key);
      const citaParts = [];
      if (f.custom_organism)   citaParts.push(`Fuente: ${f.custom_organism}`);
      if (f.custom_source_url) citaParts.push(`URL: ${f.custom_source_url}`);
      if (f.custom_notes)      citaParts.push(f.custom_notes);
      warnings.push([
        row?.l || f.source_key,
        'Manual',
        sanitizeForSpreadsheet(citaParts.length
          ? citaParts.join(' | ')
          : 'Sin fuente documentada. No apto para auditorias GHG Protocol sin respaldo adicional.'),
      ]);
    });

    if (warnings.length) {
      const ws2 = XLSX.utils.aoa_to_sheet([
        ['Fuente', 'Estado', 'Nota explicativa'],
        ...warnings,
      ]);
      ws2['!cols'] = [{ wch:35 }, { wch:16 }, { wch:80 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Advertencias');
    }

    XLSX.writeFile(wb, `factores-gei-${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  // ─── Render helpers ─────────────────────────────────────────────────────────
  const renderDot = (row) => {
    const { isCustom, std } = getRowState(row);
    if (isCustom) return <span className="gfc-sd sd-custom" title="Factor ingresado manualmente — sin trazabilidad" />;
    if (row.type === 'ren') return <span className="gfc-sd sd-ok" title="Renovables PPA · Factor fijo = 0" />;
    if (row.type === 'elec') {
      if (!siteCountry) return <span className="gfc-sd sd-warn" title="País no configurado" />;
      const e = ELEC[siteCountry];
      if (!e) return <span className="gfc-sd sd-warn" />;
      const cls = getDotCls(e.confidence, false, e.next_review);
      return <span className={`gfc-sd ${cls}`} title={`${e.org} · ${e.y} · Rev. ${e.next_review}`} />;
    }
    // comb
    if (std === 'custom') return <span className="gfc-sd sd-custom" title="Factor manual — sin trazabilidad" />;
    const meta = COMB_META[std];
    const cls  = getDotCls('alto', false, meta?.next_review_due);
    const tip  = meta
      ? `${STD_NAMES[std]} · Rev. ${meta.next_review_due || '—'}`
      : STD_NAMES[std] || std;
    return <span className={`gfc-sd ${cls}`} title={tip} />;
  };

  if (!site) {
    return <div className="gfc-msg-empty">Seleccioná un sitio para ver los factores GEI.</div>;
  }

  // ─── Main render ────────────────────────────────────────────────────────────
  return (
    <div className="gei-factors-v4">

      {/* ACTIONS */}
      <div className="gfc-actions" style={{ marginBottom: 14 }}>
        <button className="gfc-btn gfc-btn-azure" onClick={handleSaveAll}
          disabled={saving || !initialized || loading || !isDirty}
          title="Guardar cambios">
          <i className={saving ? 'spinner loading icon' : 'save icon'} />
          Guardar
        </button>
        <Dropdown
          pointing="top right"
          icon={null}
          disabled={!initialized || loading}
          trigger={
            <button className="gfc-btn gfc-btn-ghost" title="Descargar reporte" disabled={!initialized || loading}>
              <i className="download icon" />
              Descargar
            </button>
          }
          options={[
            { key:'pdf',  text:'Exportar PDF',  icon:'file pdf outline', onClick: exportPDF  },
            { key:'xlsx', text:'Exportar XLSX', icon:'file excel outline', onClick: exportXLSX },
          ]}
        />
      </div>

      {/* CONFIG CARD */}
      <div className="gfc-config-card">

        {/* Row 1: Combustion standard */}
        <div className="gfc-config-row">
          <div className="gfc-config-label">
            Estándar combustión
            <span>Aplica a Alcance 1 (gas, diesel, GLP…)</span>
          </div>
          <div className="gfc-std-pills">
            {['defra','ghg','ipcc','custom'].map(std => (
              <React.Fragment key={std}>
                <input type="radio" name="combStd" id={`cs-${std}`} className="gfc-sr"
                  value={std} checked={combStd === std} onChange={() => onCombStdChange(std)} />
                <label htmlFor={`cs-${std}`} className="gfc-sl">
                  <span className={`gfc-badge ${STD_BD[std]}`}>
                    {std==='defra'?'DEFRA':std==='ghg'?'GHG Protocol':std==='ipcc'?'IPCC':'Manual'}
                  </span>
                  {std==='defra'&&' 2025'}{std==='ghg'&&' AR6'}{std==='ipcc'&&' AR6'}
                </label>
              </React.Fragment>
            ))}
            <button className="gfc-btn gfc-btn-ghost gfc-btn-sm" onClick={() => setShowModal(true)}
              title="Cargar todos los factores de combustión desde una biblioteca estándar">
              ⬇ Inicializar por estándar
            </button>
          </div>
          {combStd !== 'custom' && (
            <div className="gfc-updated-tag">✅ Actualizado {STD_YEAR[combStd]}</div>
          )}
        </div>

        {/* Row 2: Country selector */}
        <div className="gfc-config-row gfc-country-row">
          <div className="gfc-config-label">
            País de operación
            <span>Alcance 2 — electricidad de red</span>
          </div>
          <div className="gfc-country-sel-wrap">
            <select
              className={`gfc-country-sel${!siteCountry?' empty':''}`}
              value={siteCountry}
              onChange={e => { setSiteCountry(e.target.value); setIsDirty(true); }}
            >
              <option value="">— Seleccioná el país del sitio —</option>
              <optgroup label="── Norteamérica ──">
                <option value="CA">🇨🇦 Canadá</option><option value="US">🇺🇸 Estados Unidos</option>
                <option value="MX">🇲🇽 México</option>
              </optgroup>
              <optgroup label="── Centroamérica ──">
                <option value="GT">🇬🇹 Guatemala</option><option value="SV">🇸🇻 El Salvador ⚠</option>
                <option value="HN">🇭🇳 Honduras ⚠</option><option value="NI">🇳🇮 Nicaragua ⚠</option>
                <option value="CR">🇨🇷 Costa Rica</option><option value="PA">🇵🇦 Panamá</option>
              </optgroup>
              <optgroup label="── Caribe ──">
                <option value="DO">🇩🇴 Rep. Dominicana</option><option value="HT">🇭🇹 Haití ⚠</option>
                <option value="CU">🇨🇺 Cuba ⚠</option>
              </optgroup>
              <optgroup label="── Sudamérica ──">
                <option value="AR">🇦🇷 Argentina</option><option value="BO">🇧🇴 Bolivia ⚠</option>
                <option value="BR">🇧🇷 Brasil</option><option value="CL">🇨🇱 Chile</option>
                <option value="CO">🇨🇴 Colombia</option><option value="EC">🇪🇨 Ecuador</option>
                <option value="PE">🇵🇪 Perú</option><option value="PY">🇵🇾 Paraguay</option>
                <option value="UY">🇺🇾 Uruguay</option><option value="VE">🇻🇪 Venezuela ⚠</option>
              </optgroup>
              <optgroup label="── Asia — Textil ──">
                <option value="BD">🇧🇩 Bangladesh</option><option value="CN">🇨🇳 China</option>
                <option value="ID">🇮🇩 Indonesia</option><option value="IN">🇮🇳 India</option>
                <option value="KH">🇰🇭 Camboya ⚠</option><option value="LK">🇱🇰 Sri Lanka</option>
                <option value="MM">🇲🇲 Myanmar ⚠</option><option value="MY">🇲🇾 Malasia</option>
                <option value="PK">🇵🇰 Pakistán</option><option value="TH">🇹🇭 Tailandia</option>
                <option value="VN">🇻🇳 Vietnam</option>
              </optgroup>
              <optgroup label="── Europa ──">
                <option value="DE">🇩🇪 Alemania</option><option value="ES">🇪🇸 España</option>
                <option value="FR">🇫🇷 Francia</option><option value="GB">🇬🇧 Reino Unido</option>
                <option value="IT">🇮🇹 Italia</option><option value="PT">🇵🇹 Portugal</option>
              </optgroup>
            </select>

            {siteCountry && ELEC[siteCountry] ? (() => {
              const e = ELEC[siteCountry];
              const icon = getConfIcon(e.confidence);
              return (
                <div className={`gfc-country-badge ${e.status==='proxy'?'cb-proxy':'cb-official'}`}>
                  {e.status==='proxy'&&'⚠ '}<strong>{e.f} kgCO₂e/kWh</strong>
                  {e.status==='proxy'&&' — Proxy IEA'}
                  <span className="gfc-country-detail">{icon} {e.org} · {e.y} · Rev. {e.next_review}</span>
                </div>
              );
            })() : (
              <div className="gfc-country-badge cb-none">Sin seleccionar</div>
            )}
          </div>
        </div>
      </div>

      {/* CALLOUT: IPCC / DEFRA note */}
      {(combStd === 'ipcc' || combStd === 'defra') && (
        <div className="gfc-callout gfc-callout-warn">
          <span>⚠️</span>
          <span>
            <strong>{combStd === 'ipcc' ? 'IPCC AR6' : 'DEFRA 2025'} seleccionado:</strong>{' '}
            {combStd === 'ipcc'
              ? 'IPCC publica factores físico-químicos de combustibles, pero no factores de grid eléctrico por país.'
              : 'DEFRA 2025 removió los factores de electricidad de países extranjeros.'}
            {' '}Las filas de electricidad usan el factor del país configurado arriba.
          </span>
        </div>
      )}

      {/* INFO CALLOUT */}
      <div className="gfc-callout gfc-callout-info">
        <span>ℹ️</span>
        <span>
          <strong>Dos decisiones independientes:</strong> El estándar de combustión (Alcance 1) y el
          factor eléctrico (Alcance 2) se configuran por separado. La electricidad <strong>siempre usa el
          factor del país de operación</strong>. Confianza: ✅ verificado · 🔵 plausible · ⚠ verificar
          anualmente · 🔴 pendiente · ❌ proxy IEA. Doble clic en un factor para editarlo manualmente.
        </span>
      </div>

      {/* NOT INITIALIZED BANNER */}
      {!initialized && !loading && (
        <div className="gfc-callout gfc-callout-warn">
          <span>⚠️</span>
          <span>
            Los factores GEI no fueron inicializados para este sitio.{' '}
            <button className="gfc-btn gfc-btn-azure gfc-btn-sm" onClick={handleInitialize}>
              Inicializar ahora
            </button>
          </span>
        </div>
      )}

      {loading && <div className="gfc-loading">Cargando factores…</div>}

      {/* TABLE */}
      <div className="gfc-tbl-wrap">
        <table className="gfc-table">
          <thead>
            <tr>
              <th className="tleft">Fuente</th>
              <th>Unidad</th>
              <th>Referencia</th>
              <th>Factor (kgCO₂e/ud)</th>
              <th>Alcance</th>
              <th>Vigencia</th>
              <th title="Estado">Est.</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              let curGroup = null;
              return ROWS.flatMap(row => {
                const rows = [];

                // Group header
                if (row.g !== curGroup) {
                  curGroup = row.g;
                  rows.push(
                    <tr key={`gh-${row.g}`} className={`gfc-ghdr ${row.sc===1?'sc1':'sc2'}`}>
                      <td colSpan={7}>
                        {row.g}
                        <span className={`gfc-scope-tag ${row.sc===1?'st1':'st2'}`}>Alcance {row.sc}</span>
                      </td>
                    </tr>
                  );
                }

                const sk        = row.source_key;
                const { factor, std, isCustom } = getRowState(row);
                const isElec    = row.type === 'elec';
                const isRen     = row.type === 'ren';
                const isComb    = row.type === 'comb';
                const isEditing = editing === sk;
                const effStd    = isElec ? 'pais' : std;

                // ── Reference cell ──
                let refCell;
                if (isRen) {
                  refCell = <span className="gfc-badge bd-ghg" style={{fontSize:10}}>PPA · Factor 0</span>;
                } else if (isElec) {
                  if (!siteCountry) {
                    refCell = <span className="gfc-country-chip cc-empty">⚠ Sin país configurado</span>;
                  } else {
                    const e = ELEC[siteCountry];
                    const icon = e ? getConfIcon(e.confidence) : '⚠';
                    const chipCls = e?.status === 'proxy' ? 'cc-proxy' : 'cc-set';
                    refCell = (
                      <span className={`gfc-country-chip ${chipCls}`} title={e?.org}>
                        {icon} {COUNTRY_NAMES[siteCountry] || siteCountry} · {e?.org} {e?.y}
                      </span>
                    );
                  }
                } else {
                  const rowStd = perRowStd[sk] || (isCustom ? 'custom' : combStd);
                  refCell = (
                    <div className="gfc-std-sel-wrap">
                      <select
                        className="gfc-std-sel"
                        value={rowStd}
                        style={{ borderColor: getStdBorder(rowStd) }}
                        onChange={e => onRowStdChange(row, e.target.value)}
                      >
                        {Object.entries(STD_NAMES).map(([k,v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                    </div>
                  );
                }

                // ── Factor cell ──
                const fColor = getFColor(isCustom ? 'custom' : effStd);
                const fBg    = getFBg(isCustom ? 'custom' : effStd);
                const fBrd   = getStdBorder(isCustom ? 'custom' : effStd);

                let factorCell;
                if (isEditing) {
                  factorCell = (
                    <div className="gfc-fv-wrap">
                      <input
                        className="gfc-fi" type="number" step="0.0001" min="0"
                        value={editInput}
                        onChange={e => setEditInput(e.target.value)}
                        onBlur={() => commitEdit(sk)}
                        onKeyDown={e => { if (e.key === 'Enter') commitEdit(sk); if (e.key === 'Escape') { setEditing(null); } }}
                        autoFocus
                      />
                    </div>
                  );
                } else if (factor === null || factor === undefined) {
                  factorCell = (
                    <div className="gfc-fv-wrap">
                      <span className="gfc-fv gfc-fv-empty">Sin país</span>
                    </div>
                  );
                } else {
                  factorCell = (
                    <div className="gfc-fv-wrap">
                      <span
                        className="gfc-fv"
                        style={{ color: fColor, background: fBg, borderColor: fBrd }}
                        onDoubleClick={() => isComb && enableEdit(sk)}
                        title={isComb ? 'Doble clic para editar manualmente' : undefined}
                      >
                        {Number(factor).toFixed(4)}
                      </span>
                      {isComb && (
                        <button className="gfc-eb" onClick={() => enableEdit(sk)} title="Editar">✎</button>
                      )}
                    </div>
                  );
                }

                // ── Scope badge ──
                const scBg    = row.sc===1 ? '#FEF3C7' : '#DBEAFE';
                const scColor = row.sc===1 ? '#92400E' : '#1E40AF';

                // ── Vigencia ──
                let vigencia;
                if (isRen) {
                  vigencia = <span style={{fontSize:11,color:'var(--gfc-g400)'}}>—</span>;
                } else if (isElec && siteCountry && ELEC[siteCountry]) {
                  const e = ELEC[siteCountry];
                  const vc = e.status==='official' ? 'var(--gfc-green)' : 'var(--gfc-orange)';
                  vigencia = <span style={{fontSize:11,fontWeight:600,color:vc}}>{e.y}</span>;
                } else {
                  const yr = STD_YEAR[isCustom ? 'custom' : std] || '—';
                  const vc2 = isCustom ? 'var(--gfc-c-custom)' : 'var(--gfc-green)';
                  vigencia = <span style={{fontSize:11,fontWeight:600,color:vc2}}>{yr}</span>;
                }

                rows.push(
                  <tr key={sk}>
                    <td className="gfc-td-src">{row.l}</td>
                    <td><code className="gfc-unit">{row.u}</code></td>
                    <td>{refCell}</td>
                    <td>{factorCell}</td>
                    <td>
                      <span className="gfc-sb" style={{background:scBg,color:scColor}}>
                        Alcance {row.sc}
                      </span>
                    </td>
                    <td>{vigencia}</td>
                    <td>{renderDot(row)}</td>
                  </tr>
                );

                return rows;
              });
            })()}
          </tbody>
        </table>
      </div>

      {/* LEGEND */}
      <div className="gfc-legend">
        {['defra','ghg','ipcc','pais','custom'].map(k => (
          <div key={k} className="gfc-li">
            <span className={`gfc-badge ${STD_BD[k]}`}>
              {k==='defra'?'DEFRA 2025':k==='ghg'?'GHG Protocol AR6':k==='ipcc'?'IPCC AR6':k==='pais'?'País':'Manual'}
            </span>
          </div>
        ))}
        <div className="gfc-li"><span className="gfc-sd sd-ok" /> Vigente</div>
        <div className="gfc-li"><span className="gfc-sd sd-warn" /> Verificar anualmente</div>
        <div className="gfc-li"><span className="gfc-sd sd-custom" /> Manual / sin trazabilidad</div>
      </div>

      {/* MODAL: inicializar estándar */}
      {showModal && (
        <div className="gfc-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="gfc-edit-modal">

            <div className="gfc-edit-modal__header">
              <p className="gfc-edit-modal__title">Inicializar por estándar</p>
              <p className="gfc-edit-modal__sub">Carga todos los factores de combustión (Alcance 1) desde el estándar elegido</p>
            </div>

            <div className="gfc-em-section">
              <p className="gfc-em-section-title">Estándar para combustión</p>
              <div className="gfc-std-cards">
                {[
                  {k:'ghg',   n:'GHG Protocol AR6', o:'WRI / WBCSD · 2023',             d:'Estándar global. Base del Higg FEM. Recomendado para verificaciones externas.'},
                  {k:'defra', n:'DEFRA 2025',        o:'UK Dept. for Environment',        d:'Requerido por marcas europeas y GRI. Gas Natural: 2.04542 kgCO₂e/m³.'},
                  {k:'ipcc',  n:'IPCC AR6',          o:'Panel Intergubernamental · 2021', d:'Factores físico-químicos universales. Exigido por CSRD/EFRAG.'},
                  {k:'custom',n:'Manual',            o:'Sin referencia externa',           d:'Ingresás cada valor manualmente. Sin trazabilidad automática en reportes.'},
                ].map(({k,n,o,d}) => (
                  <div key={k} className={`gfc-sc${modalStd===k?' sel':''}`} onClick={() => setModalStd(k)}>
                    <div className="gfc-sc-n">{n}</div>
                    <div className="gfc-sc-o">{o}</div>
                    <div className="gfc-sc-d">{d}</div>
                    <span className={`gfc-badge ${STD_BD[k]}`} style={{marginTop:8,display:'inline-flex'}}>
                      {k==='defra'?'DEFRA 2025':k==='ghg'?'AR6 · 2023':k==='ipcc'?'AR6 · 2021':'Manual'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="gfc-edit-modal__warn">
              <span>⚠</span>
              <span>La electricidad de red <strong>NO</strong> se inicializa desde aquí — siempre usa el factor del país configurado arriba.</span>
            </div>

            <div className="gfc-em-section">
              <p className="gfc-em-section-title">
                Vista previa · <span className={`gfc-badge ${STD_BD[modalStd]}`}>{STD_NAMES[modalStd]}</span>
              </p>
              <table className="gfc-pvw">
                <thead>
                  <tr>
                    <th>Fuente (Alcance 1)</th>
                    <th>Factor</th>
                    <th>Unidad</th>
                    <th>Nota</th>
                  </tr>
                </thead>
                <tbody>
                  {(PREVIEW[modalStd]||PREVIEW.ghg).map(([src,f,u,note],i) => (
                    <tr key={i}>
                      <td>{src}</td>
                      <td style={{textAlign:'center',fontFamily:'monospace',fontWeight:700}}>{f}</td>
                      <td style={{textAlign:'center',fontFamily:'monospace',fontSize:10}}>{u}</td>
                      <td style={{fontSize:10,color:'var(--gfc-orange)'}}>{note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="gfc-anote" style={{marginTop:10}}>
                Sustemia registrara el estandar, version, fecha y usuario configurador.
                Aparecera en el encabezado de reportes PDF/XLSX del sitio.
              </div>
            </div>

            <div className="gfc-edit-modal__footer">
              <p className="gfc-edit-modal__note" />
              <div className="gfc-edit-modal__actions">
                <button className="gfc-em-btn gfc-em-btn-primary" onClick={handleApplyModal}>
                  Aplicar estandar
                </button>
                <button className="gfc-em-btn gfc-em-btn-cancel" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* MODAL: edición manual con cita obligatoria */}
      {editModal && (
        <div className="gfc-overlay" onClick={e => e.target === e.currentTarget && setEditModal(null)}>
          <div className="gfc-edit-modal">

            <div className="gfc-edit-modal__header">
              <p className="gfc-edit-modal__title">Edición manual</p>
              <p className="gfc-edit-modal__sub">
                Fuente: {ROWS.find(r => r.source_key === editModal.sk)?.l || editModal.sk}
              </p>
            </div>

            <div className="gfc-edit-modal__warn">
              <span>⚠</span>
              <span>Este valor <strong>reemplaza</strong> el factor de biblioteca y queda registrado como manual en todos los reportes. Documentá la fuente para que sea auditable.</span>
            </div>

            <div className="gfc-em-section">
              <p className="gfc-em-section-title">Factor</p>
              <div className="gfc-em-field">
                <label>Valor (kgCO₂e/ud)<span className="req">*</span></label>
                <input type="number" step="0.0001" min="0"
                  value={editModal.value} autoFocus
                  onChange={e => setEditModal(m => ({ ...m, value: e.target.value }))}
                />
              </div>
            </div>

            <div className="gfc-em-section">
              <p className="gfc-em-section-title">Cita de la fuente</p>
              <div className="gfc-em-field">
                <label>Organismo / fuente<span className="req">*</span></label>
                <input type="text"
                  placeholder="Ej: ARPEL 2024, IPCC Tabla 2.3..."
                  value={editModal.organism}
                  onChange={e => setEditModal(m => ({ ...m, organism: e.target.value }))}
                />
              </div>
              <div className="gfc-em-field">
                <label>URL de referencia<span className="req">*</span></label>
                <input type="url"
                  placeholder="https://..."
                  value={editModal.source_url}
                  onChange={e => setEditModal(m => ({ ...m, source_url: e.target.value }))}
                />
              </div>
              <div className="gfc-em-field">
                <label>Notas / justificación</label>
                <textarea
                  placeholder="Por qué se usa este valor en lugar del estándar de biblioteca..."
                  value={editModal.notes}
                  onChange={e => setEditModal(m => ({ ...m, notes: e.target.value }))}
                />
              </div>
            </div>

            <div className="gfc-edit-modal__footer">
              <p className="gfc-edit-modal__note"><span className="req">*</span> campos obligatorios</p>
              <div className="gfc-edit-modal__actions">
                <button className="gfc-em-btn gfc-em-btn-primary" onClick={commitEditModal}>
                  Aplicar valor manual
                </button>
                <button className="gfc-em-btn gfc-em-btn-cancel" onClick={() => setEditModal(null)}>
                  Cancelar
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
